import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../AppIcon';


/**
 * ThemeToggle — compact 3-way toggle: Light / System / Dark
 * Variants:
 *   - "pill"   (default): horizontal pill with 3 icon buttons
 *   - "icon": single icon button cycling through modes *   -"select" : dropdown select
 */
const ThemeToggle = ({ variant = 'pill', className = '' }) => {
  const { preference, setTheme, isDark } = useTheme();

  const options = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'system', icon: Monitor, label: 'System' },
    { value: 'dark', icon: Moon, label: 'Dark' },
  ];

  if (variant === 'icon') {
    const cycle = { light: 'system', system: 'dark', dark: 'light' };
    const current = options?.find(o => o?.value === preference) || options?.[1];
    const Icon = current?.icon;
    return (
      <button
        onClick={() => setTheme(cycle?.[preference] || 'system')}
        title={`Theme: ${current?.label} — click to cycle`}
        className={`p-2 rounded-lg transition-colors duration-200
          ${isDark
            ? 'text-slate-300 hover:text-white hover:bg-white/10' :'text-slate-500 hover:text-[#1a365d] hover:bg-slate-100'
          } ${className}`}
        aria-label="Toggle theme"
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  }

  if (variant === 'select') {
    return (
      <select
        value={preference}
        onChange={e => setTheme(e?.target?.value)}
        className={`text-sm rounded-lg border px-3 py-1.5 transition-colors duration-200
          ${isDark
            ? 'bg-slate-800 border-slate-600 text-slate-200' :'bg-white border-slate-200 text-slate-700'
          } ${className}`}
        aria-label="Select theme"
      >
        {options?.map(o => (
          <option key={o?.value} value={o?.value}>{o?.label}</option>
        ))}
      </select>
    );
  }

  // Default: pill
  return (
    <div
      className={`inline-flex items-center rounded-xl p-1 gap-0.5 transition-colors duration-200
        ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100 border border-slate-200'}
        ${className}`}
      role="group"
      aria-label="Theme preference"
    >
      {options?.map(({ value, icon: Icon, label }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            onClick={() => setTheme(value)}
            title={label}
            aria-pressed={active}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200
              ${active
                ? isDark
                  ? 'bg-slate-600 text-white shadow-sm'
                  : 'bg-white text-[#1a365d] shadow-sm'
                : isDark
                  ? 'text-slate-400 hover:text-slate-200' :'text-slate-500 hover:text-slate-700'
              }`}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
